import { useTranslation } from "react-i18next";

import { Form } from "@av/components/atoms/form/Form";
import { Modal } from "@av/components/atoms/Modal";
import { Typography } from "@av/components/atoms/typography/Typography";
import {
  getCompanyDocumentValidationError,
  getFileListFromEvent,
  getNativeFile,
  validatePdfFile,
} from "../company-document-form";
import { MODAL_WIDTH } from "../constants";
import { CompanyKnowledgeUpload } from "./CompanyKnowledgeUpload";
import { ModalActions } from "./ModalActions";
import { useFileError } from "../hooks/useFileError";

import type {
  CompanyKnowledgeDocument,
  DocumentFormValues,
  ReplaceCompanyDocumentValues,
} from "../types";

type UploadVersionModalProps = {
  document?: CompanyKnowledgeDocument;
  isSaving?: boolean;
  onCancel: () => void;
  onRenew: (values: ReplaceCompanyDocumentValues) => Promise<void>;
};

export function UploadVersionModal({
  document,
  isSaving,
  onCancel,
  onRenew,
}: UploadVersionModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm<Pick<DocumentFormValues, "file">>();
  const { fileError, setFileError, clearFileError } = useFileError();
  const fileErrorId = "company-knowledge-renew-file-error";

  const handleCancel = () => {
    onCancel();
    form.resetFields();
    clearFileError();
  };

  return (
    <Modal
      destroyOnHidden
      footer={null}
      okText={t("platform.adminSettings.companyKnowledge_renew")}
      onCancel={handleCancel}
      open={!!document}
      testIdPrefix="company-knowledge-renew-document"
      title={t("platform.adminSettings.companyKnowledge_uploadNewVersion")}
      width={MODAL_WIDTH}
    >
      <Form
        form={form}
        initialValues={{ file: [] }}
        onFieldsChange={(changedFields) => {
          const fileField = changedFields.find((field) => field.name[0] === "file");

          if (fileField) {
            setFileError(fileField.errors?.[0]);
          }
        }}
        onFinish={async (values) => {
          const file = getNativeFile(values.file?.[0]);

          if (!document || !file) {
            return;
          }

          try {
            await onRenew({ document, file });
            form.resetFields();
            clearFileError();
          } catch (error) {
            setFileError(
              getCompanyDocumentValidationError(error, "filename") ??
                t("platform.adminSettings.companyKnowledge_uploadFailed")
            );
            // Keep the modal open so the user can retry.
          }
        }}
      >
        <Typography.Text testIdPrefix="company-knowledge-renew-document-hint">
          {t("platform.adminSettings.companyKnowledge_uploadNewVersionHint")}
        </Typography.Text>
        <Form.Item
          name="file"
          noStyle
          getValueFromEvent={getFileListFromEvent}
          rules={[{ validator: (_rule, value) => validatePdfFile(t, value) }]}
          testIdPrefix="company-knowledge-renew-document-file"
          validateTrigger={["onChange", "onSubmit"]}
          valuePropName="fileList"
        >
          <CompanyKnowledgeUpload
            errorId={fileErrorId}
            fileInputTestId="company-knowledge-renew-document-file-input"
            fileError={fileError}
            testIdPrefix="company-knowledge-renew-document-upload"
          />
        </Form.Item>
        {fileError && (
          <Typography.Text
            id={fileErrorId}
            role="alert"
            testIdPrefix="company-knowledge-renew-document-file-error"
            type="danger"
          >
            {fileError}
          </Typography.Text>
        )}
        <ModalActions
          cancelTestIdPrefix="company-knowledge-renew-document-cancel"
          isSaving={isSaving}
          okText={t("platform.adminSettings.companyKnowledge_renew")}
          okTestIdPrefix="company-knowledge-renew-document-submit"
          onCancel={handleCancel}
        />
      </Form>
    </Modal>
  );
}
